import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useTheme } from "@/hooks/useTheme";

function getCirclesPerSource() {
  if (typeof window === "undefined") return 50;
  if (window.innerWidth < 768) return 30;
  return 50;
}

export function InterferenceBackground({ scrollProgress }: { scrollProgress: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();
  const { theme } = useTheme();
  const scrollRef = useRef(scrollProgress);
  scrollRef.current = scrollProgress;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let time = 0;

    const isMobile = window.innerWidth < 768;
    const circlesPerSource = getCirclesPerSource();
    const dpr = Math.min(window.devicePixelRatio, 2);
    const frameInterval = isMobile ? 33 : 22;
    let lastFrameTime = 0;

    const isDark = document.documentElement.classList.contains("dark");
    const bgFill = isDark ? "#0a0a0a" : "#FFFFFF";
    const strokeRgb = isDark ? "255, 255, 255" : "0, 0, 0";

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = (now: number) => {
      if (frameInterval && now - lastFrameTime < frameInterval) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }
      lastFrameTime = now;

      ctx.fillStyle = bgFill;
      ctx.fillRect(0, 0, width, height);

      const scroll = scrollRef.current;

      const s1x = width * (0.35 + Math.cos(time * 0.0002) * 0.06);
      const s1y = height * (0.45 + Math.sin(time * 0.00015) * 0.06);
      const s2x = width * (0.65 + Math.cos(time * 0.00025 + 2) * 0.06);
      const s2y = height * (0.55 + Math.sin(time * 0.0002 + 1) * 0.06);

      const maxRadius = Math.max(width, height) * 0.9;
      const radiusStep = maxRadius / circlesPerSource;

      ctx.lineWidth = 0.8;

      // Source 1
      for (let i = 1; i < circlesPerSource; i++) {
        const t = i / circlesPerSource;
        const r = i * radiusStep;
        const alpha = 0.03 + (1 - t) * 0.09;

        ctx.strokeStyle = `rgba(${strokeRgb}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(s1x, s1y, r, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Source 2
      for (let i = 1; i < circlesPerSource; i++) {
        const t = i / circlesPerSource;
        const baseR = i * radiusStep;
        const r = baseR + Math.cos(t * Math.PI * 4 + time * 0.0003) * (2 + scroll * 3);
        const alpha = 0.03 + (1 - t) * 0.09;

        ctx.strokeStyle = `rgba(${strokeRgb}, ${alpha})`;
        ctx.beginPath();
        ctx.arc(s2x, s2y, Math.max(0, r), 0, Math.PI * 2);
        ctx.stroke();
      }

      if (!reducedMotion) {
        time += 16;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion, theme]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        mixBlendMode: "var(--bg-blend-mode)" as React.CSSProperties["mixBlendMode"],
      }}
      aria-hidden="true"
    />
  );
}
